import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Fuse from 'fuse.js';
import { postsAPI, usersAPI } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import PostListView from '../../components/PostListView/PostListView';
import './Search.css';

const useQueryParam = (key) => {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || '';
  }, [location.search, key]);
};

const Search = () => {
  const q = useQueryParam('q');
  const query = String(q || '').trim();

  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const NAV_PAGES = useMemo(() => ([
    { label: 'Inicio', path: '/' },
    { label: 'Explorar', path: '/explore' },
    { label: 'Biblioteca', path: '/library' },
    { label: 'Magazine', path: '/magazine' },
    { label: 'TV', path: '/tv' },
    { label: 'Tienda', path: '/store' },
    { label: 'Juegos', path: '/games' },
    { label: 'Academia', path: '/academy' },
    { label: 'Favoritos', path: '/favorites' },
    { label: 'Ayuda', path: '/help' }
  ]), []);

  const pagesFuse = useMemo(() => {
    return new Fuse(NAV_PAGES, {
      keys: ['label', 'path'],
      threshold: 0.4,
      ignoreLocation: true,
      minMatchCharLength: 1
    });
  }, [NAV_PAGES]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!query) {
        setPosts([]);
        setUsers([]);
        setPages([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const [postsRes, usersRes] = await Promise.all([
          postsAPI.search(query),
          usersAPI.search(query).catch(() => ({ data: { users: [] } }))
        ]);

        const postsData = postsRes?.data;
        const nextPosts = Array.isArray(postsData) ? postsData : (postsData?.posts || []);

        const usersData = usersRes?.data;
        const nextUsers = Array.isArray(usersData) ? usersData : (usersData?.users || []);

        const nextPages = pagesFuse.search(query, { limit: 6 }).map(r => r.item);

        if (!cancelled) {
          setPosts(nextPosts);
          setUsers(nextUsers);
          setPages(nextPages);
        }
      } catch (e) {
        if (!cancelled) {
          setError('No se pudo buscar. Intenta de nuevo.');
          setPosts([]);
          setUsers([]);
          setPages([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [query, pagesFuse]);

  return (
    <div className="search-page">
      <div className="search-page-header">
        <h1 className="search-title">Búsqueda</h1>
        {query ? (
          <p className="search-subtitle">Resultados para: <span className="search-query">{query}</span></p>
        ) : (
          <p className="search-subtitle">Escribe algo en el buscador.</p>
        )}
      </div>

      {loading && (
        <div className="search-state">Buscando…</div>
      )}

      {!loading && error && (
        <div className="search-state search-error">{error}</div>
      )}

      {!loading && !error && query && posts.length === 0 && users.length === 0 && pages.length === 0 && (
        <div className="search-state">No hay resultados.</div>
      )}

      {pages.length > 0 && (
        <div className="search-section">
          <h2 className="search-section-title">Páginas</h2>
          <div className="search-pages">
            {pages.map((p) => (
              <Link key={p.path} to={p.path} className="search-page-link">
                {p.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {users.length > 0 && (
        <div className="search-section">
          <h2 className="search-section-title">Usuarios</h2>
          <div className="search-users">
            {users.map((u) => (
              <Link key={u.id} to={`/${u.username || u.id}`} className="search-user">
                {u.avatar ? (
                  <img className="search-user-avatar" src={resolveMediaUrl(u.avatar)} alt={u.name || u.username || 'Usuario'} />
                ) : (
                  <div className="search-user-avatar" aria-label="Avatar">
                    {(u.name || u.username || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="search-user-info">
                  <div className="search-user-name">{u.name || u.username || 'Usuario'}</div>
                  <div className="search-user-handle">@{u.username || u.id}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {posts.length > 0 && (
        <div className="search-section">
          <h2 className="search-section-title">Contenido</h2>
          <div className="search-results">
            {posts.map((post) => (
              <PostListView key={post.id} post={post} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
